// Manages user-picked download destination folders via the File System Access API.
// Handles are persisted in IndexedDB so they survive reloads. Each folder is scoped
// to a "scope" key (usually a clubId) so every club can have its own download folder.
// A `null`/omitted scope maps to the legacy global folder for backwards compatibility.
// Only Chromium-based browsers currently expose window.showDirectoryPicker.

const DB_NAME = "clubfin-fs";
const STORE = "handles";
const GLOBAL_KEY = "download-folder";

function keyFor(scope) {
  return scope ? `download-folder:${scope}` : GLOBAL_KEY;
}

// Feature detection.
export function isFolderPickerSupported() {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

// Cross-tab notification for folder buttons — subscribers receive (name, scope).
const listeners = new Set();
export function onFolderChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function notify(name, scope) {
  for (const cb of listeners) {
    try { cb(name, scope || null); } catch { /* noop */ }
  }
}

// --- IndexedDB helpers ---
function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function idbDelete(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Verify (and optionally request) readwrite permission for a persisted handle.
async function ensurePermission(handle, request = false) {
  if (!handle || !handle.queryPermission) return true; // legacy safety
  const opts = { mode: "readwrite" };
  const state = await handle.queryPermission(opts);
  if (state === "granted") return true;
  if (!request) return false;
  const req = await handle.requestPermission(opts);
  return req === "granted";
}

export async function getSavedFolderName(scope) {
  if (!isFolderPickerSupported()) return null;
  try {
    const handle = await idbGet(keyFor(scope));
    return handle?.name || null;
  } catch {
    return null;
  }
}

export async function pickFolder(scope) {
  if (!isFolderPickerSupported()) {
    throw new Error("unsupported");
  }
  const handle = await window.showDirectoryPicker({ mode: "readwrite" });
  const ok = await ensurePermission(handle, true);
  if (!ok) throw new Error("permission-denied");
  await idbSet(keyFor(scope), handle);
  notify(handle.name, scope);
  return handle.name;
}

export async function clearFolder(scope) {
  await idbDelete(keyFor(scope));
  notify(null, scope);
}

// Attempts to write a Blob into the saved folder. Returns true on success,
// false when there is no folder set / permission was denied. The caller
// should fall back to a regular browser download in that case.
export async function saveBlobToFolder(blob, filename, scope) {
  if (!isFolderPickerSupported()) return false;
  let handle;
  try {
    handle = await idbGet(keyFor(scope));
    // Backwards compatibility: if a scoped folder isn't set, fall back to the
    // legacy global folder so existing users don't lose their setup.
    if (!handle && scope) handle = await idbGet(GLOBAL_KEY);
  } catch {
    return false;
  }
  if (!handle) return false;
  const ok = await ensurePermission(handle, true);
  if (!ok) return false;
  try {
    const fileHandle = await handle.getFileHandle(filename, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch (e) {
    // Directory revoked / disk error / user removed folder.
    console.warn("saveBlobToFolder failed", e);
    return false;
  }
}

// Regular browser download fallback.
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// One-liner used by call sites: try folder first, fall back to the download prompt.
export async function saveOrDownload(blob, filename, scope) {
  const wrote = await saveBlobToFolder(blob, filename, scope);
  if (!wrote) downloadBlob(blob, filename);
  return wrote;
}

// React hook: returns the current saved folder name (or null) for a given scope,
// and re-renders when that scope's folder is picked/cleared.
import { useEffect, useState } from "react";
export function useDownloadFolderName(scope) {
  const [name, setName] = useState(null);
  useEffect(() => {
    let alive = true;
    getSavedFolderName(scope).then((n) => { if (alive) setName(n); });
    const off = onFolderChange((n, s) => {
      if ((s || null) === (scope || null)) setName(n);
    });
    return () => { alive = false; off?.(); };
  }, [scope]);
  return name;
}
